import { call, put, takeLatest } from "redux-saga/effects";
import request from "utils/request";
import { TalentAgenciesActions } from "./index";
import { showToast } from "components/ToastContainer/index";

function* fetchTalentAgencySaga(action) {
  try {
    const {
      page = 1,
      limit = 10,
      search = "",
      leads_opted_status = ""
    } = action.payload || {};

    const queryParams = new URLSearchParams({ page, limit });
    if (search) queryParams.append("search", search);
    if (leads_opted_status)
      queryParams.append("leads_opted_status", leads_opted_status);

    const response = yield call(
      request,
      `/api/talent-agencies?${queryParams.toString()}`,
      {
        method: "GET",
        credentials: "include"
      }
    );

    if (response.success) {
      yield put(
        TalentAgenciesActions.fetchTalentAgencyListSuccess(response.data)
      );
    }
  } catch (error) {
    yield put(TalentAgenciesActions.fetchTalentAgencyListError(error.message));
  }
}

function* fetchTalentAgencyMembersSaga(action) {
  try {
    const {
      agencyId,
      type,
      page,
      limit,
      manager_id,
      search,
      roster_updated_at
    } = action.payload;

    const queryParams = new URLSearchParams({ page, limit, type });

    if (manager_id) {
      queryParams.append("manager_id", manager_id); // ADD
    }

    if (search) {
      queryParams.append("search", search); // ADD
    }

    const response = yield call(
      request,
      `/api/talent-agencies/${agencyId}/talent-agency-members?${queryParams.toString()}`,
      {
        method: "GET",
        credentials: "include"
      }
    );

    if (response.success) {
      yield put(
        TalentAgenciesActions.fetchTalentAgencyMembersSuccess({
          ...response.data,
          type
        })
      );
    }
  } catch (error) {
    yield put(
      TalentAgenciesActions.fetchTalentAgencyMembersError(error.message)
    );
  }
}

function* fetchManagerDropdownSaga(action) {
  try {
    const { agencyId } = action.payload;

    const response = yield call(
      request,
      `/api/talent-agencies/${agencyId}/managers/creator-count`,
      {
        method: "GET",
        credentials: "include"
      }
    );
    if (response.success) {
      yield put(
        TalentAgenciesActions.fetchManagerDropdownSuccess(response.data)
      );
    }
  } catch (error) {
    yield put(TalentAgenciesActions.fetchManagerDropdownError(error.message));
  }
}

function* addTalentAgencyManagerSaga(action) {
  try {
    const { payload } = action;

    const response = yield call(
      request,
      "/api/talent-agencies/add-talent-agency-member",
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      }
    );

    if (response.success) {
      yield put(
        TalentAgenciesActions.addTalentAgencyManagerSuccess(response.data)
      );

      showToast(
        response?.message || "Team member added successfully!",
        "success"
      );
    }
  } catch (error) {
    yield put(TalentAgenciesActions.addTalentAgencyManagerError(error.message));
    showToast(error?.message || "Add failed", "danger");
  }
}

function* updateTeamMemberSaga(action) {
  try {
    const { payload } = action;

    const response = yield call(
      request,
      `/api/talent-agencies/update-team-members`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      }
    );

    if (response.success) {
      const updatedData = {
        id: payload.userId || payload.id,
        ...response.changed_fields?.reduce?.(
          (acc, field) => ({ ...acc, [field.key]: field.value }),
          {}
        )
      };

      yield put(TalentAgenciesActions.updateTeamMemberSuccess(updatedData));
      showToast(
        response?.message || "Updated the Team Member successfully!",
        "success"
      );
    }
  } catch (error) {
    yield put(TalentAgenciesActions.updateTeamMemberError(error.message));
    showToast(error?.message || "Update failed", "danger");
  }
}

function* removeTeamMemberSaga(action) {
  try {
    const response = yield call(
      request,
      "/api/talent-agencies/remove-team-members",
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(action.payload)
      }
    );

    yield put(TalentAgenciesActions.removeTeamMemberSuccess(response));
  } catch (error) {
    yield put(TalentAgenciesActions.removeTeamMemberError(error.message));
  }
}

function* fetchManagersListSaga(action) {
  try {
    const { talent_company_id, exclude_manager_id } = action.payload;
    const query = new URLSearchParams({ talent_company_id });
    if (exclude_manager_id) {
      query.append("exclude_manager_id", exclude_manager_id);
    }

    const response = yield call(
      request,
      `/api/talent-agencies/managers-list?${query.toString()}`,
      {
        method: "GET",
        credentials: "include"
      }
    );

    if (response.success) {
      yield put(
        TalentAgenciesActions.fetchManagersListSuccess(response.managers)
      );
    }
  } catch (error) {
    yield put(TalentAgenciesActions.fetchManagersListError(error.message));
  }
}

function* reassignCreatorsSaga(action) {
  try {
    const { payload } = action;

    const response = yield call(
      request,
      `/api/talent-agencies/managers/transfer-creators`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      }
    );

    if (response.success) {
      yield put(
        TalentAgenciesActions.reassignCreatorsToManagerSuccess(response.data)
      );
      showToast(
        response?.message || "Creators reassigned successfully!",
        "success"
      );
    }
  } catch (error) {
    yield put(
      TalentAgenciesActions.reassignCreatorsToManagerError(error.message)
    );
    showToast(error?.message || "Reassignment failed", "danger");
  }
}

export function* talentAgenciesSaga() {
  yield takeLatest(
    TalentAgenciesActions.fetchTalentAgencyList.type,
    fetchTalentAgencySaga
  );
  yield takeLatest(
    TalentAgenciesActions.fetchTalentAgencyMembers.type,
    fetchTalentAgencyMembersSaga
  );
  yield takeLatest(
    TalentAgenciesActions.fetchManagerDropdown.type,
    fetchManagerDropdownSaga
  );
  yield takeLatest(
    TalentAgenciesActions.addTalentAgencyManager.type,
    addTalentAgencyManagerSaga
  );
  yield takeLatest(
    TalentAgenciesActions.updateTeamMember.type,
    updateTeamMemberSaga
  );
  yield takeLatest(
    TalentAgenciesActions.removeTeamMember.type,
    removeTeamMemberSaga
  );
  yield takeLatest(
    TalentAgenciesActions.fetchManagersList.type,
    fetchManagersListSaga
  );
  yield takeLatest(
    TalentAgenciesActions.reassignCreatorsToManager.type,
    reassignCreatorsSaga
  );
}
